/** Pure math behind the listing price range slider (`SwRangeSlider.vue`). */

const MIN_PERCENT = 0;
const MAX_PERCENT = 100;
const KEYBOARD_STEP_PERCENT = 1;
const KEYBOARD_PAGE_STEP_PERCENT = 10;

const clampPercent = (
	percent: number,
	lower: number = MIN_PERCENT,
	upper: number = MAX_PERCENT
): number => Math.min(Math.max(percent, lower), upper);

const percentToValue = (percent: number, min: number, max: number): number =>
	Math.round(min + ((max - min) * percent) / MAX_PERCENT);

const valueToPercent = (value: number, min: number, max: number): number => {
	if (max === min) return MIN_PERCENT;
	return ((value - min) / (max - min)) * MAX_PERCENT;
};

const pointerToPercent = (
	clientX: number,
	track: { left: number; width: number }
): number => {
	if (!track.width) return MIN_PERCENT;
	return clampPercent(((clientX - track.left) / track.width) * MAX_PERCENT);
};

const getKeyboardStep = (key: string): number | null => {
	switch (key) {
		case "ArrowRight":
		case "ArrowUp":
			return KEYBOARD_STEP_PERCENT;
		case "ArrowLeft":
		case "ArrowDown":
			return -KEYBOARD_STEP_PERCENT;
		case "PageUp":
			return KEYBOARD_PAGE_STEP_PERCENT;
		case "PageDown":
			return -KEYBOARD_PAGE_STEP_PERCENT;
		default:
			return null;
	}
};

/**
 * Next percent for a slider key press, or `null` when the key is not handled.
 * A step is never smaller than `unitPercent` (one whole price unit), so a
 * key press always changes the rounded value on a narrow range.
 */
const getKeyboardPercent = (
	key: string,
	current: number,
	unitPercent = 0
): number | null => {
	if (key === "Home") return MIN_PERCENT;
	if (key === "End") return MAX_PERCENT;
	const step = getKeyboardStep(key);
	if (step === null) return null;
	return current + Math.sign(step) * Math.max(Math.abs(step), unitPercent);
};

interface CurrencyAffixes {
	Prefix?: string | null;
	Suffix?: string | null;
}

/**
 * Slider `aria-valuetext` from the value as the field shows it ("1 799"),
 * with the currency where it belongs: "$ 100", "1 799 kr", "0 kr".
 */
const formatValueText = (
	shownValue: string,
	currency?: CurrencyAffixes | null
): string => {
	const prefix = currency?.Prefix?.trim();
	const suffix = currency?.Suffix?.trim();
	return [prefix, shownValue, suffix].filter((part) => part).join(" ");
};

type RangeMarker = "left" | "right";

/**
 * Which marker is drawn on top. Stacked at an end only one of them can move
 * (left at 100%, right at 0%), so that one must be reachable; otherwise the
 * last moved marker stays on top.
 */
const getTopMarker = (
	leftPercent: number,
	rightPercent: number,
	lastMoved: RangeMarker
): RangeMarker => {
	if (leftPercent === rightPercent && rightPercent >= MAX_PERCENT)
		return "left";
	if (leftPercent === rightPercent && leftPercent <= MIN_PERCENT)
		return "right";
	return lastMoved;
};

/** Whether a typed value may be committed: a number within `[lower, upper]`. */
const isWithinRange = (
	value: number | null,
	lower: number,
	upper: number
): value is number => value !== null && value >= lower && value <= upper;

/** Same `[min, max]` by value; no range and an empty range are the same. */
const isSameRange = (a?: number[], b?: number[]): boolean =>
	a?.[0] === b?.[0] && a?.[1] === b?.[1];

/**
 * A `type="number"` input value; decimals are kept for the caller to round
 * per field. Empty or non-numeric → null, never 0.
 */
const parseNumberInput = (raw: string): number | null => {
	if (!raw.trim()) return null;
	const value = Number(raw);
	return Number.isFinite(value) ? value : null;
};

export type { RangeMarker };
export {
	MAX_PERCENT,
	MIN_PERCENT,
	clampPercent,
	formatValueText,
	getKeyboardPercent,
	getTopMarker,
	isSameRange,
	isWithinRange,
	parseNumberInput,
	percentToValue,
	pointerToPercent,
	valueToPercent
};
